import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RoleType } from "./roleType";
import { RootState } from "../../store";
import { enhancedApiClient, isSuccessResponse } from "../../services/enhancedApiClient";

interface roleTypeSlice {
  roleTypes: RoleType[];
  formData: Partial<RoleType>;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
}

const initialState: roleTypeSlice = {
  roleTypes: [],
  formData: { role_name: "", status: 1 },
  loading: false,
  error: null,
  pagination: null,
};

// Get list
export const fetchRoleTypes = createAsyncThunk(
  "roleType/fetchRoleTypes",
  async (params?: { page?: number; limit?: number; search?: string }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);

      const response = await enhancedApiClient.get("/master/role-type?" + queryParams.toString());
      if (isSuccessResponse(response)) {
        console.log("Fetch role types response:", response.data);
        return response.data;
      } else {
        return rejectWithValue("Failed to fetch role types");
      }
    } catch (error: any) {
      console.error("Fetch role types error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch role types";
      return rejectWithValue(errorMessage);
    }
  }
);

// Create Role Type
export const createRoleType = createAsyncThunk(
  "roleType/createRoleType",
  async (data: Partial<RoleType>, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.post("/master/role-type", data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to create role type");
      }
    } catch (error: any) {
      console.error("Create role type error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create role type";
      return rejectWithValue(errorMessage);
    }
  }
);

// Edit Role Type
export const editRoleType = createAsyncThunk(
  "roleType/editRoleType",
  async ({ id, data }: { id: string; data: Partial<RoleType> }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.put(`/master/role-type/${id}`, data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to update role type");
      }
    } catch (error: any) {
      console.error("Edit role type error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update role type";
      return rejectWithValue(errorMessage);
    }
  }
);

// Toggle role type status
export const toggleRoleTypeStatus = createAsyncThunk(
  "roleType/toggleRoleTypeStatus",
  async ({ id, status }: { id: number; status: number }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.patch(`/master/role-type/${id}/status`, { status });

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to toggle role type status");
      }
    } catch (error: any) {
      console.error("Toggle role type status error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to toggle role type status";
      return rejectWithValue(errorMessage);
    }
  }
);

export const roleTypeSlice = createSlice({
  name: "roleType",
  initialState,
  reducers: {
    setRoleName: (state, action: PayloadAction<string>) => {
      state.formData.role_name = action.payload;
    },
    setRoleTypeStatus: (state, action: PayloadAction<number>) => {
      state.formData.status = action.payload;
    },
    resetForm: (state) => {
      state.formData = { role_name: "", status: 1 };
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchRoleTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoleTypes.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both response structures: { data: { records, pagination } } or { records, pagination }
        const payloadData = action.payload.data || action.payload;
        const records = payloadData?.records || [];
        const pagination = payloadData?.pagination || null;
        
        state.roleTypes = records.map((record: any) => ({
          id: String(record.roleTypeId || record.id),
          roleTypeId: record.roleTypeId || record.id,
          role_name: record.roleName || record.role_name,
          display_name: record.displayName || record.display_name,
          displayName: record.displayName || record.display_name,
          user_name: record.userName,
          status: record.status,
          updatedOn: record.updatedOn,
          createdOn: record.createdOn,
          updated_by: record.updatedBy,
          action: record.action,
        }));
        state.pagination = pagination;
        console.log("Role types fetched successfully:", action.payload);
      })
      .addCase(fetchRoleTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create
      .addCase(createRoleType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRoleType.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Handle both response structures: { data: {...} } or {...}
        const payloadData = action.payload?.data || action.payload;
        if (payloadData) {
          const newRoleType: RoleType = {
            id: String(payloadData.roleTypeId || payloadData.id),
            roleTypeId: payloadData.roleTypeId || payloadData.id,
            role_name: payloadData.roleName || payloadData.role_name,
            display_name: payloadData.displayName || payloadData.display_name,
            displayName: payloadData.displayName || payloadData.display_name,
            status: payloadData.status,
            updatedOn: payloadData.updatedOn,
            createdOn: payloadData.createdOn,
            updated_by: payloadData.updatedBy,
            action: payloadData.action,
          };
          state.roleTypes.unshift(newRoleType);
        }
        state.formData = { role_name: "", status: 1 };
      })
      .addCase(createRoleType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Edit
      .addCase(editRoleType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editRoleType.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Handle both response structures: { data: {...} } or {...}
        const payloadData = action.payload?.data || action.payload;
        if (payloadData) {
          const updatedRoleType: RoleType = {
            id: String(payloadData.roleTypeId || payloadData.id),
            roleTypeId: payloadData.roleTypeId || payloadData.id,
            role_name: payloadData.roleName || payloadData.role_name,
            display_name: payloadData.displayName || payloadData.display_name,
            displayName: payloadData.displayName || payloadData.display_name,
            status: payloadData.status,
            updatedOn: payloadData.updatedOn,
            createdOn: payloadData.createdOn,
            updated_by: payloadData.updatedBy,
            action: payloadData.action,
          };
          const index = state.roleTypes.findIndex((r) => r.id === updatedRoleType.id);
          if (index !== -1) {
            state.roleTypes[index] = updatedRoleType;
          }
        }
        state.formData = { role_name: "", status: 1 };
      })
      .addCase(editRoleType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Toggle Status
      .addCase(toggleRoleTypeStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleRoleTypeStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Handle both response structures: { data: {...} } or {...}
        const payloadData = action.payload?.data || action.payload;
        if (payloadData) {
          const updatedRoleType: RoleType = {
            id: String(payloadData.roleTypeId || payloadData.id),
            roleTypeId: payloadData.roleTypeId || payloadData.id,
            role_name: payloadData.roleName || payloadData.role_name,
            display_name: payloadData.displayName || payloadData.display_name,
            displayName: payloadData.displayName || payloadData.display_name,
            status: payloadData.status,
            updatedOn: payloadData.updatedOn,
            createdOn: payloadData.createdOn,
            updated_by: payloadData.updatedBy,
            action: payloadData.action,
          };
          const index = state.roleTypes.findIndex((r) => r.id === updatedRoleType.id);
          if (index !== -1) {
            state.roleTypes[index] = updatedRoleType;
          }
        }
      })
      .addCase(toggleRoleTypeStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setRoleName, setRoleTypeStatus, resetForm } = roleTypeSlice.actions;
// Selectors
export const selectedRoleTypes = (state: RootState) => state.roleType.roleTypes;
export const selectRoleTypeLoading = (state: RootState) => state.roleType.loading;
export const selectRoleTypeError = (state: RootState) => state.roleType.error;
export const selectRoleTypePagination = (state: RootState) => state.roleType.pagination;

export default roleTypeSlice.reducer;

